import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { BarPlot } from '@mui/x-charts/BarChart';
import type { AllSeriesType } from '@mui/x-charts/models/seriesType';
import { useTranslation } from 'react-i18next';

import type { ChartDataProvider, ChartRequest, DashboardBarKey, Period, SalesChannel, SalesRep, StandardSeriesKeys } from './DashboardTypes';
import { getDisplayLabels, getRollingDashboardLabels, sumArray } from './DashboardHelpers';
import { DASHBOARD_COLORS } from './DashboardColors';

export type DashboardGraphProps = {
  period: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep: boolean;
  selectedSalesReps: string[];
  salesRepsData: SalesRep[];
  dataProvider: ChartDataProvider;
  height?: number;
};

type LegendItem = { id: string; label: string; color?: string };

const SOURCE_AGNOSTIC_BARS: DashboardBarKey[] = [
  'purchasesValueBar',
  'toShipBar',
  'shippedBar',
  'returnsBar',
  'creditNotesBar',
  'creditNotesValueBar',
];

const toLegendItems = (series: AllSeriesType[]): LegendItem[] => (
  (series || [])
    .filter(s => (s as any)?.type === 'bar')
    .map(s => ({
      id: String((s as any).id ?? ''),
      label: String((s as any).label ?? (s as any).id ?? ''),
      color: (s as any).color as string | undefined,
    }))
    .filter(item => item.id && item.label)
);

const OutsideLegend = React.forwardRef<HTMLDivElement, { series: AllSeriesType[] }>(({ series }, ref) => {
  const items = useMemo(() => toLegendItems(series), [series]);
  if (!items.length) return null;

  return (
    <Box ref={ref} sx={{ px: 1, pt: 1, pb: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {items.map(item => (
          <Box key={item.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 150, maxWidth: 360 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: item.color ?? 'currentColor', flex: '0 0 auto' }} />
            <Typography variant="caption" noWrap title={item.label} sx={{ maxWidth: 320 }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
});
OutsideLegend.displayName = 'OutsideLegend';

const repColors = ['#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', '#edc948'];

const buildRepSeries = (
  labels: string[],
  repData: Record<string, number[]>,
  req: ChartRequest,
  orientation: 'vertical' | 'horizontal',
  t: (key: string, options?: any) => string,
): AllSeriesType[] => {
  return req.allReps
    .filter(rep => req.selectedReps.includes(rep.id))
    .map((rep, index) => {
      const data = repData[rep.id] || Array.from({ length: labels.length }, () => 0);
      const total = sumArray(data).toLocaleString('sv-SE');
      return {
        type: 'bar',
        id: rep.id,
        data,
        label: t('{{repName}} ({{total}} st)', { repName: rep.name, total }),
        color: repColors[index % repColors.length],
        ...(orientation === 'vertical' ? { yAxisKey: 'quantities' } : { xAxisKey: 'quantities', layout: 'horizontal' }),
        valueFormatter: (value: number | null) => `${(value ?? 0).toLocaleString('sv-SE')} ${t('st')}`,
      } as AllSeriesType;
    });
};

const buildStandardSeries = (buckets: Record<StandardSeriesKeys, number[]>, req: ChartRequest, orientation: 'vertical' | 'horizontal', t: (key: string, options?: any) => string): AllSeriesType[] => {
  const include = (barKey: DashboardBarKey) => req.selectedBars.includes(barKey);
  const includeBySource = (barKey: DashboardBarKey, channel: SalesChannel) => SOURCE_AGNOSTIC_BARS.includes(barKey) || req.selectedChannels.includes(channel);

  const mk = (id: string, label: string, data: number[], color: string, axis: 'quantities' | 'money', stack?: string): AllSeriesType => ({
    type: 'bar',
    id,
    data,
    label,
    color,
    ...(orientation === 'vertical' ? { yAxisKey: axis } : { xAxisKey: axis, layout: 'horizontal' }),
    valueFormatter: (value: number | null) => axis === 'money'
      ? `${(value ?? 0).toLocaleString('sv-SE')} ${t('kr')}`
      : `${(value ?? 0).toLocaleString('sv-SE')} ${t('st')}`,
    ...(stack ? { stack } : {}),
  } as AllSeriesType);

  const series: Array<AllSeriesType | false> = [
    include('ordersBar') && includeBySource('ordersBar', 'Store') && mk('ordersBarStore', t('Orders Store ({{total}} st)', { total: sumArray(buckets.ordersStore).toLocaleString('sv-SE') }), buckets.ordersStore, DASHBOARD_COLORS.orders.store, 'quantities', 'ordersBar'),
    include('ordersBar') && includeBySource('ordersBar', 'Online') && mk('ordersBarOnline', t('Orders Online ({{total}} st)', { total: sumArray(buckets.ordersOnline).toLocaleString('sv-SE') }), buckets.ordersOnline, DASHBOARD_COLORS.orders.online, 'quantities', 'ordersBar'),
    include('ordersValueBar') && includeBySource('ordersValueBar', 'Store') && mk('ordersValueBarStore', t('Orders Value Store ({{total}} kr)', { total: sumArray(buckets.ordersValueStore).toLocaleString('sv-SE') }), buckets.ordersValueStore, DASHBOARD_COLORS.orderValue.store, 'money', 'ordersValueBar'),
    include('ordersValueBar') && includeBySource('ordersValueBar', 'Online') && mk('ordersValueBarOnline', t('Orders Value Online ({{total}} kr)', { total: sumArray(buckets.ordersValueOnline).toLocaleString('sv-SE') }), buckets.ordersValueOnline, DASHBOARD_COLORS.orderValue.online, 'money', 'ordersValueBar'),
    include('purchasesValueBar') && mk('purchasesValueBar', t('Purchases Value ({{total}} kr)', { total: sumArray(buckets.purchasesValueOnline).toLocaleString('sv-SE') }), buckets.purchasesValueOnline, DASHBOARD_COLORS.purchasing, 'money'),
    include('toShipBar') && mk('toShipBar', t('To Ship ({{total}} st)', { total: sumArray(buckets.toShipOnline).toLocaleString('sv-SE') }), buckets.toShipOnline, DASHBOARD_COLORS.toShip, 'quantities'),
    include('shippedBar') && mk('shippedBar', t('Shipped ({{total}} st)', { total: sumArray(buckets.shippedOnline).toLocaleString('sv-SE') }), buckets.shippedOnline, DASHBOARD_COLORS.shipped, 'quantities'),
    include('returnsBar') && mk('returnsBar', t('Returns ({{total}} st)', { total: sumArray(buckets.returnsOnline).toLocaleString('sv-SE') }), buckets.returnsOnline, DASHBOARD_COLORS.returns, 'quantities'),
    include('creditNotesBar') && mk('creditNotesBar', t('Credit Notes Qty ({{total}} st)', { total: sumArray(buckets.creditNotesOnline).toLocaleString('sv-SE') }), buckets.creditNotesOnline, DASHBOARD_COLORS.credits, 'quantities'),
    include('creditNotesValueBar') && mk('creditNotesValueBar', t('Credit Notes Value ({{total}} kr)', { total: sumArray(buckets.creditNotesValueOnline).toLocaleString('sv-SE') }), buckets.creditNotesValueOnline, DASHBOARD_COLORS.creditsValue, 'money'),
  ];

  return series.filter(Boolean) as AllSeriesType[];
};

type OrderChartBaseProps = {
  period: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep: boolean;
  selectedSalesReps: string[];
  salesRepsData: SalesRep[];
  dataProvider: ChartDataProvider;
  comparison?: boolean;
  orientation: 'vertical' | 'horizontal';
  height: number;
};

const OrderChartBase: React.FC<OrderChartBaseProps> = ({
  period,
  selectedBars,
  selectedSalesChannels,
  viewBySalesRep,
  selectedSalesReps,
  salesRepsData,
  dataProvider,
  comparison = false,
  orientation,
  height,
}) => {
  const { t } = useTranslation();
  const labels = useMemo(() => getRollingDashboardLabels(period), [period]);
  const displayLabels = useMemo(() => getDisplayLabels(period, labels), [period, labels]);

  const comparisonLabels = useMemo(() => labels.map(label => t('Prev {{label}}', { label })), [labels, t]);
  const displayComparisonLabels = useMemo(() => displayLabels.map(label => t('Prev {{label}}', { label })), [displayLabels, t]);
  const interleave = (a: string[], b: string[]) => {
    const output: string[] = [];
    for (let i = 0; i < Math.min(a.length, b.length); i += 1) output.push(b[i], a[i]);
    return output;
  };

  const xLabels = useMemo(
    () => (comparison && !viewBySalesRep && orientation === 'vertical')
      ? interleave(displayLabels, displayComparisonLabels)
      : displayLabels,
    [comparison, displayComparisonLabels, displayLabels, orientation, viewBySalesRep],
  );

  const req: ChartRequest = {
    period,
    selectedBars,
    selectedChannels: selectedSalesChannels,
    byRep: viewBySalesRep,
    selectedReps: selectedSalesReps,
    allReps: salesRepsData,
    orientation,
  };

  const repData = useMemo(
    () => (viewBySalesRep ? dataProvider.getSalesRepSeries(req, labels) : {} as Record<string, number[]>),
    [dataProvider, labels, req, viewBySalesRep],
  );
  const bucketData = useMemo(
    () => (!viewBySalesRep ? dataProvider.getStandardBuckets(req, labels) : {} as Record<StandardSeriesKeys, number[]>),
    [dataProvider, labels, req, viewBySalesRep],
  );

  const series = useMemo<AllSeriesType[]>(() => {
    if (viewBySalesRep) return buildRepSeries(labels, repData, req, orientation, t);
    return buildStandardSeries(bucketData, req, orientation, t);
  }, [bucketData, labels, orientation, repData, req, t, viewBySalesRep]);

  const hasMoneyAxis = selectedBars.some(bar => ['ordersValueBar', 'purchasesValueBar', 'creditNotesValueBar'].includes(bar));
  const legendRef = useRef<HTMLDivElement | null>(null);
  const [legendHeight, setLegendHeight] = useState(48);

  useEffect(() => {
    const element = legendRef.current;
    if (!element) return;

    let rafId = 0;
    let resizeObserver: ResizeObserver | null = null;

    const measure = () => {
      rafId = 0;
      const measuredHeight = Math.ceil(element.getBoundingClientRect().height || 0);
      setLegendHeight(previous => (Math.abs(previous - measuredHeight) <= 1 ? previous : measuredHeight));
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(measure);
    };

    schedule();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => schedule());
      resizeObserver.observe(element);
    } else {
      const timeout = window.setTimeout(() => schedule(), 50);
      return () => window.clearTimeout(timeout);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [series]);

  const chartHeight = Math.max(280, height - legendHeight);
  const barGroupCount = useMemo(() => {
    const bars = series.filter(seriesEntry => (seriesEntry as any)?.type === 'bar');
    const groups = new Set(bars.map(seriesEntry => String((seriesEntry as any).stack ?? (seriesEntry as any).id)));
    return groups.size;
  }, [series]);

  const bandAxisSizing = useMemo(
    () => (barGroupCount <= 1 ? { categoryGapRatio: 0.7, barGapRatio: 0.2 } : { categoryGapRatio: 0.25, barGapRatio: 0.1 }),
    [barGroupCount],
  );

  return (
    <Box sx={{ width: '100%', height }}>
      <OutsideLegend ref={legendRef} series={series} />
      <ResponsiveChartContainer
        height={chartHeight}
        series={series}
        xAxis={orientation === 'vertical'
          ? [{ id: 'xAxis', data: xLabels, scaleType: 'band', hideTooltip: true, ...bandAxisSizing }]
          : [{ id: 'quantities', label: t('Antal'), position: 'bottom' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: t('SEK'), position: 'top' } as any] : [])]}
        yAxis={orientation === 'vertical'
          ? [{ id: 'quantities', label: t('Antal'), position: 'left' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: '', position: 'right' } as any] : [])]
          : [{ id: 'yAxis', data: displayLabels, scaleType: 'band', hideTooltip: true, ...bandAxisSizing }]}
        margin={orientation === 'horizontal'
          ? { top: hasMoneyAxis ? 40 : 20, left: 80, right: 20, bottom: 30 }
          : { top: 20, left: 60, right: hasMoneyAxis ? 80 : 20, bottom: 30 }}
        sx={{ '& .MuiLineElement-root': { strokeWidth: 4 }, '& .MuiChartsAxis-line': { strokeWidth: 4 } }}
      >
        <ChartsTooltip followCursor trigger="item" />
        <BarPlot />
        {orientation === 'vertical'
          ? <><ChartsXAxis axisId="xAxis" /><ChartsYAxis axisId="quantities" position="left" />{hasMoneyAxis && <ChartsYAxis axisId="money" position="right" />}</>
          : <><ChartsYAxis axisId="yAxis" /><ChartsXAxis axisId="quantities" position="bottom" />{hasMoneyAxis && <ChartsXAxis axisId="money" position="top" />}</>}
      </ResponsiveChartContainer>
    </Box>
  );
};

export const DashboardGraph: React.FC<DashboardGraphProps> = ({ height = 520, ...props }) => (
  <OrderChartBase {...props} orientation="vertical" height={height} />
);

export const DashboardGraphMobile: React.FC<DashboardGraphProps> = ({ height = 660, ...props }) => (
  <OrderChartBase {...props} orientation="horizontal" height={height} />
);
